import { useMemo, useState } from 'react';
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from 'recharts';
import type { Account, Snapshot } from '../types';
import { fmtCompact, fmtDate, fmtMoney } from '../utils/format';
import { SegmentedControl } from '../ui';

interface Props {
  series: { date: string; netWorth: number; investable: number; liabilities: number }[];
  snapshots: Snapshot[];
  accounts: Account[];
}

const SUMMARY_COLORS: Record<string, string> = {
  netWorth: '#3b54e8',
  investable: '#059669',
  liabilities: '#ef4444',
};

const PALETTE = ['#f59e0b', '#8b5cf6', '#0891b2', '#db2777', '#14b8a6', '#6366f1', '#a16207', '#64748b'];

export function HistoryChart({ series, snapshots, accounts }: Props) {
  const [visible, setVisible] = useState<Record<string, boolean>>({
    netWorth: true,
    investable: true,
    liabilities: false,
  });
  const [mode, setMode] = useState<'summary' | 'accounts'>('summary');

  // Per-account time series with forward-fill
  const accountSeries = useMemo(() => {
    const sorted = [...snapshots].sort((a, b) => (a.date < b.date ? -1 : 1));
    const carry: Record<string, number> = {};
    const rows: Record<string, number | string>[] = [];
    for (const s of sorted) {
      Object.assign(carry, s.balances);
      const row: Record<string, number | string> = { date: s.date, t: new Date(s.date).getTime() };
      for (const a of accounts) {
        if (a.archived) continue;
        row[a.id] = carry[a.id] ?? 0;
      }
      rows.push(row);
    }
    return rows;
  }, [snapshots, accounts]);

  const summaryData = useMemo(
    () => series.map(s => ({ ...s, t: new Date(s.date).getTime() })),
    [series],
  );

  const data = mode === 'summary' ? summaryData : accountSeries;

  const keys: { key: string; label: string; color: string }[] = useMemo(() => {
    if (mode === 'summary') {
      return [
        { key: 'netWorth', label: 'Net Worth', color: SUMMARY_COLORS.netWorth },
        { key: 'investable', label: 'Investable', color: SUMMARY_COLORS.investable },
        { key: 'liabilities', label: 'Liabilities', color: SUMMARY_COLORS.liabilities },
      ];
    }
    return accounts
      .filter(a => !a.archived)
      .map((a, i) => ({ key: a.id, label: a.name, color: PALETTE[i % PALETTE.length] }));
  }, [mode, accounts]);

  function toggle(k: string) {
    setVisible(v => ({ ...v, [k]: !(v[k] ?? true) }));
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 px-1">
        <h2 className="text-sm font-semibold">Actual history</h2>
        <SegmentedControl
          size="sm"
          value={mode}
          onChange={setMode}
          options={[
            { value: 'summary', label: 'Summary' },
            { value: 'accounts', label: 'Accounts' },
          ]}
        />
      </div>

      <div className="h-64 w-full">
        <ResponsiveContainer>
          <ComposedChart data={data as object[]} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#eef0f4" strokeDasharray="3 6" vertical={false} />
            <XAxis
              dataKey="t"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
              tickFormatter={(v: number) => {
                const d = new Date(v);
                return d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
              }}
              tick={{ fill: '#828a9c', fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: '#dde1e9' }}
            />
            <YAxis
              tickFormatter={v => fmtCompact(v)}
              tick={{ fill: '#828a9c', fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: '#dde1e9' }}
              width={52}
            />
            <Tooltip
              cursor={{ stroke: '#525b6f', strokeDasharray: '2 4' }}
              content={<HistoryTooltip visibleKeys={keys.filter(k => visible[k.key] ?? true)} />}
            />
            {keys
              .filter(k => visible[k.key] ?? true)
              .map(k => (
                <Line
                  key={k.key}
                  type="monotone"
                  dataKey={k.key}
                  name={k.label}
                  stroke={k.color}
                  strokeWidth={2}
                  dot={{ r: 3, fill: k.color }}
                  activeDot={{ r: 5, strokeWidth: 2, fill: '#fff', stroke: k.color }}
                  isAnimationActive={false}
                />
              ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="flex flex-wrap gap-1.5 px-1">
        {keys.map(k => {
          const on = visible[k.key] ?? true;
          return (
            <button
              key={k.key}
              type="button"
              onClick={() => toggle(k.key)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition ${
                on
                  ? 'border-ink-200 bg-white text-ink-900 dark:border-ink-700 dark:bg-ink-800 dark:text-ink-50'
                  : 'border-ink-100 bg-ink-50 text-ink-400 dark:border-ink-800 dark:bg-ink-900 dark:text-ink-300'
              }`}
              aria-pressed={on}
            >
              <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: on ? k.color : '#cbd5e1' }} />
              {k.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function HistoryTooltip({
  active,
  payload,
  label,
  visibleKeys,
}: TooltipProps<number, string> & { visibleKeys: { key: string; label: string; color: string }[] }) {
  if (!active || !payload || payload.length === 0) return null;
  const iso = new Date(Number(label)).toISOString().slice(0, 10);
  return (
    <div className="rounded-xl border border-ink-200 bg-white/98 p-3 shadow-card backdrop-blur dark:bg-ink-800/98 dark:border-ink-700">
      <div className="mb-2 border-b border-ink-100 pb-1.5 text-xs font-mono font-semibold tabular-nums dark:border-ink-700">
        {fmtDate(iso)}
      </div>
      <table className="min-w-[180px] text-xs">
        <tbody>
          {visibleKeys.map(k => {
            const entry = payload.find(p => p.dataKey === k.key);
            if (!entry) return null;
            return (
              <tr key={k.key}>
                <td className="pr-3 py-0.5">
                  <span className="mr-1.5 inline-block h-2 w-2 rounded-full align-middle" style={{ backgroundColor: k.color }} />
                  <span className="text-ink-700 dark:text-ink-100">{k.label}</span>
                </td>
                <td className="py-0.5 text-right font-mono font-semibold tabular-nums">{fmtMoney(entry.value as number)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
